import { LoaderFunctionArgs } from "react-router";
import { Link, useLoaderData } from "react-router";
import { DataBlock } from "~/components/data-block";
import { PairsTable } from "~/components/pairs-table";
import { fetchPrices } from "~/lib/astrolescent";
import { setDbFromContext } from "~/lib/db";
import { getDexes } from "~/lib/models/dex";
import { getPairs } from "~/lib/models/pair";
import { fetchRewardPools } from "~/lib/radix-rewards";
import { prettyValue } from "~/lib/util";

export async function loader({ context }: LoaderFunctionArgs) {
  setDbFromContext(context);

  const allPairs = await getPairs();
  const pairs = allPairs.filter((p) => p.Pair.dexAddress !== "component_rdx1cq34ta4ssvtyxm3vf0l4eleunt7uz9ha5wyzrc965xqafdueadpy4x");
  const prices = await fetchPrices();
  const totals = await getDexes();

  const rewardPairs = await fetchRewardPools();

  pairs.sort((a, b) => {
    if (a.Pair.tvlUSD < b.Pair.tvlUSD) {
      return 1;
    } else if (a.Pair.tvlUSD > b.Pair.tvlUSD) {
      return -1;
    }

    return 0;
  });

  // calc volumes
  const volume24H = pairs.reduce(
    (total, p) => total + (p.Pair.baseVolume24H * (prices[p.Token.address]?.tokenPriceUSD || 0) + p.Pair.quoteVolume24H * prices[p.Pair.quoteToken]?.tokenPriceUSD || 0) / 2,
    0,
  );

  // calc tvl
  const totalTVL = pairs.reduce((total, p) => total + (p.Pair.tvlUSD || 0), 0);

  return ({
    pairs,
    rewardPairs,
    totalTVL,
    volume24H,
    volumeTotal: totals.reduce((total, d) => total + d.totalVolumeUSD, 0),
    feesTotal: totals.reduce((total, d) => total + d.totalFeesUSD, 0),
  });
}

export default function LiquidityAddIndex() {
  const { pairs, rewardPairs, totalTVL, volume24H, volumeTotal, feesTotal } = useLoaderData<typeof loader>();

  return (
    <>
      <div className="pb-4 text-center lg:py-16">
        <h1 className="text-3xl font-bold lg:text-4xl">DefiPlaza reduces the risk of Impermanent Loss</h1>
        <h2 className="text-lg mt-1">The next-generation AMM built to reduce IL and unlock higher, more consistent LP returns.</h2>
      </div>


      <div className="flex items-center justify-between">
        <h1 className="py-4 text-left text-3xl font-bold lg:p-8 lg:text-4xl">Pools</h1>
        <Link to="/liquidity/create" className="btn-white-small">
          Create New Pool
        </Link>
      </div>

      <dl className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-4">
        <DataBlock title="Total Value Locked" data={`$${prettyValue(totalTVL)}`} />
        <DataBlock title="24H Volume" data={`$${prettyValue(volume24H)}`} />
        <DataBlock title="Total Trade Volume" data={`$${prettyValue(volumeTotal)}`} />
        <DataBlock title="Total Fees" data={`$${prettyValue(feesTotal)}`} />
      </dl>

      <div className="dark:bg-background-900 mt-4 grow space-y-4 rounded-xl bg-white p-4 lg:mt-0 lg:rounded-2xl lg:p-8 lg:pt-6">
        <PairsTable pairs={pairs} rewardPairs={rewardPairs} />
      </div>
    </>
  );
}
